import React from "react";
import _ from "lodash";

import {
  ExtensionURLs,
  getValueTypeByAttribute,
  getOverrideAuditExtensionURLs,
  getOverrideRestoreAuditExtensionURLs,
  getRestoreExtensionURLs,
  ActionLabels,
  BirthDateOverrideURLs,
  BirthDateRestoreURLs,
  GenderOverrideURLs,
  GenderRestoreURLs,
  MultipleBirthOverrideURLs,
  MultipleBirthRestoreURLs,
} from "../GoldenRecordExclusion/Utils";

export const createOverrideAuditExtension = (auditDetails, attribute) => {
  const overrideAuditExtensions = getOverrideAuditExtensionURLs(attribute);
  const { username, incidentNumber, moreInfo } = auditDetails;
  const valueType = getValueTypeByAttribute();

  return [
    {
      url: overrideAuditExtensions.overriddenBy,
      [valueType]: username,
    },
    {
      url: overrideAuditExtensions.overrideReason,
      [valueType]: `${incidentNumber} - ${moreInfo}`,
    },
    {
      url: overrideAuditExtensions.overrideTimestamp,
      valueDateTime: getFormattedTimestamp(),
    },
  ];
};

export const createRestoreAuditExtensions = (auditDetails, attribute) => {
  const restoreAuditExtensions =
    getOverrideRestoreAuditExtensionURLs(attribute);
  const { username, incidentNumber, moreInfo } = auditDetails;
  const valueType = getValueTypeByAttribute();

  return [
    {
      url: restoreAuditExtensions.restoredBy,
      [valueType]: username,
    },
    {
      url: restoreAuditExtensions.restoreReason,
      [valueType]: `${incidentNumber} - ${moreInfo}`,
    },
    {
      url: restoreAuditExtensions.restoreTimestamp,
      valueDateTime: getFormattedTimestamp(),
    },
  ];
};

const overrideUrl = (attribute) => {
  switch (attribute) {
    case "birthDate":
      return ExtensionURLs.birthDateOverride;
      break;
    case "gender":
      return ExtensionURLs.genderOverride;
      break;
    case "multipleBirthBoolean":
      return ExtensionURLs.multipleBirthBooleanOverride;
      break;
    case "userName":
      return ExtensionURLs.overriddenBy;
      break;
    case "reason":
      return ExtensionURLs.overrideReason;
      break;
    case "timestamp":
      return ExtensionURLs.overrideTimestamp;
      break;
    default:
      return ExtensionURLs.genericOverride;
      break;
  }
};

function getFormattedTimestamp() {
  // Get the ISO string, remove milliseconds and 'Z', then add '+00:00'
  return new Date().toISOString().replace(/\.\d{3}Z$/, "+00:00");
}

const PrepareOverrides = (record, attributeOverrides, auditDetails) => {
  const patientRecord = JSON.parse(JSON.stringify(record));
  const overrides = { ...attributeOverrides };

  const requestPayload = {
    resourceType: patientRecord.resourceType,
    id: patientRecord.id,
    meta: {
      ...patientRecord.meta,
      lastUpdated: "",
    },
  };

  // Helper function to remove restore extensions from an item when it's being overridden.
  const removeRestoreExtensions = (item, attributeName) => {
    if (!item.extension) return item;

    const restoreURLs = getRestoreExtensionURLs(attributeName);
    const restoreUrlValues = Object.values(restoreURLs);

    item.extension = item.extension.filter(
      (ext) =>
        !restoreUrlValues.some((url) => ext.url && ext.url.includes(url)),
    );

    return item;
  };

  // Helper function to create an override extension for a given attribute.
  const createOverrideExtension = (detailMap) => {
    const attributeName = detailMap.originalFieldName;
    const originalItemContent = _.cloneDeep(detailMap.originalFieldDataCopy);
    const valueType = getValueTypeByAttribute(attributeName);

    const overrideExtension = { url: overrideUrl(attributeName) };
    overrideExtension[valueType] = removeRestoreExtensions(originalItemContent);

    return overrideExtension;
  };

  // Helper function to push an item into an array in the request payload.
  // Use the key value if it exists, otherwise initialize it as an empty array.
  const pushObject = (object, key, item) => {
    object[key] = object[key] || new Array();
    object[key].push(item);
  };

  // Adding all previous overrides and restores to the request payload by looping through all keys in the patient record
  Object.keys(patientRecord).forEach((key) => {
    if (patientRecord[key] instanceof Array) {
      if (key === "extension") {
        // multipleBirth, gender and birthDate are all single valued and their overrides/restores exist in the top level extension
        patientRecord["extension"]
          .filter((e) => e.hasOwnProperty("url"))
          .forEach((e) => {
            // Collect all audit extensions for birthDate
            const birthDateAuditUrls = [
              ...Object.values(BirthDateOverrideURLs),
              ...Object.values(BirthDateRestoreURLs),
            ];
            if (
              _.isEmpty(overrides.birthDate) &&
              birthDateAuditUrls.includes(e["url"])
            ) {
              pushObject(requestPayload, "extension", e);
              requestPayload["birthDate"] = patientRecord["birthDate"];
            }
            // Collect all audit extensions for gender
            const genderAuditUrls = [
              ...Object.values(GenderOverrideURLs),
              ...Object.values(GenderRestoreURLs),
            ];
            if (
              _.isEmpty(overrides.gender) &&
              genderAuditUrls.includes(e["url"])
            ) {
              pushObject(requestPayload, "extension", e);
              requestPayload["gender"] = patientRecord["gender"];
            }
            // Collect all audit extensions for multipleBirthBoolean
            const multipleBirthAuditUrls = [
              ...Object.values(MultipleBirthOverrideURLs),
              ...Object.values(MultipleBirthRestoreURLs),
            ];
            if (
              _.isEmpty(overrides.multipleBirth) &&
              multipleBirthAuditUrls.includes(e["url"])
            ) {
              pushObject(requestPayload, "extension", e);
              requestPayload["multipleBirthBoolean"] =
                patientRecord["multipleBirthBoolean"];
            }
          });
      } else {
        // all other attributes are multi-valued and their overrides exist in the extension of each item
        patientRecord[key].forEach((item) => {
          // Check if this item matches any override's originalFieldDataCopy - if so, skip it as we'll add the new override below
          const isBeingOverridden = overrides[key]?.some((override) =>
            _.isEqual(item, override.originalFieldDataCopy),
          );

          if (!isBeingOverridden) {
            item.hasOwnProperty("extension") &&
              item["extension"].some(
                (e) =>
                  ExtensionURLs.genericOverride.includes(e.url) ||
                  Object.values(getRestoreExtensionURLs(key)).includes(e.url),
              ) &&
              pushObject(requestPayload, key, item);
          }
        });
      }
    }
  });

  // Adding all new overrides to the request payload by looping through all keys in the attributeOverrides object
  Object.keys(overrides).forEach((key) => {
    overrides[key].forEach((detailMap) => {
      const attributeName = detailMap.originalFieldName;
      const action = detailMap["Mark as"];
      const overrideExtension = createOverrideExtension(detailMap);
      const overrideAuditExtension = createOverrideAuditExtension(
        auditDetails,
        attributeName,
      );

      if (
        ["birthDate", "gender", "multipleBirthBoolean"].includes(attributeName)
      ) {
        // Adding the created override extension to the top level extension and adding the attribute value to the request payload.
        pushObject(requestPayload, "extension", overrideExtension);
        //push override audit extensions as well inside extension
        overrideAuditExtension.forEach((ext) => {
          requestPayload["extension"].push(ext);
        });
        requestPayload[attributeName] = detailMap.originalFieldData;
      } else {
        // Create a deep copy to avoid mutating the original and causing circular references
        const attributeObject = _.cloneDeep(detailMap.originalFieldData);
        removeRestoreExtensions(attributeObject, attributeName);

        // Adding the created override extension to the attribute object and adding the attribute object to the request
        // payload for each action type. Inactive types need to have their use set to "old" and invalid types do not have
        // new values so an empty object is created.
        if (action === "Replace") {
          pushObject(attributeObject, "extension", overrideExtension);
          //push override audit extensions as well inside extension
          overrideAuditExtension.forEach((ext) => {
            attributeObject["extension"].push(ext);
          });
          pushObject(requestPayload, attributeName, attributeObject);
        }
        if (action === "Inactive") {
          attributeObject["use"] = "old";
          pushObject(attributeObject, "extension", overrideExtension);
          //push override audit extensions as well inside extension
          overrideAuditExtension.forEach((ext) => {
            attributeObject["extension"].push(ext);
          });
          pushObject(requestPayload, attributeName, attributeObject);
        }
        if (action === "Invalid") {
          const invalidObject = {};
          invalidObject["extension"] = new Array();
          pushObject(invalidObject, "extension", overrideExtension);
          //push override audit extensions as well inside extension
          overrideAuditExtension.forEach((ext) => {
            invalidObject["extension"].push(ext);
          });
          pushObject(requestPayload, attributeName, invalidObject);
        }
      }
    });
  });

  return requestPayload;
};

export default PrepareOverrides;
